import { getCookie } from '@calm/web-common/dist/cookies';
import isSSR from '@calm/web-common/dist/isSSR';
import { Environment } from '@rhg/constants';
import useAppConfig from '@rhg/hooks/use-app-config';
import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

export const E2E_ACCESS_TOKEN = 'e2eAccessToken';
export const E2E_USER_ID = 'e2eUserId';

const environmentsSupportingE2eAuth = [
    Environment.LOCAL,
    Environment.DEV,
    Environment.STAGING,
] as string[];

type E2eAuthValue =
    | {
          getAccessTokenSilently: () => Promise<string>;
          getIdTokenClaims: () => Promise<{ __raw: string }>;
          isAuthenticated: boolean;
          isLoading: boolean;
          loginWithRedirect: () => void;
          logout: () => void;
          user: { sub: string; email: string };
          error: null;
      }
    | undefined;

export const E2eAuthContext = createContext<E2eAuthValue>(undefined);

export const useE2eAuth = () => useContext(E2eAuthContext);

/**
 * Provides E2E test authentication state derived from cookies.
 * Runs the isLoading timeout effect once at app startup.
 *
 * Only active in Local, Dev, and Staging environments when
 * e2eAccessToken and e2eUserId cookies are present.
 */
export const E2eAuthProvider = ({ children }: { children: ReactNode }) => {
    const { NEXT_PUBLIC_ENV } = useAppConfig();
    const [accessToken] = useState<string | undefined>(
        !isSSR() ? getCookie(E2E_ACCESS_TOKEN) : undefined
    );
    const [userId] = useState<string | undefined>(
        !isSSR() ? getCookie(E2E_USER_ID) : undefined
    );
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const id = setTimeout(() => setIsLoading(false), 0);
        return () => clearTimeout(id);
    }, []);

    const value = useMemo<E2eAuthValue>(() => {
        if (
            !accessToken ||
            !userId ||
            !environmentsSupportingE2eAuth.includes(NEXT_PUBLIC_ENV)
        ) {
            return undefined;
        }

        const user = {
            sub: userId,
            email: 'test-email@example.com',
        };

        return {
            getAccessTokenSilently: () => Promise.resolve(accessToken),
            getIdTokenClaims: () => Promise.resolve({ __raw: accessToken }),
            isAuthenticated: !isLoading,
            isLoading,
            loginWithRedirect: () => {
                alert(
                    'You are currently using the e2e cookie as an access token. If you would like to test login on staging you will need to clear your cookies'
                );
            },
            logout: () => {
                alert(
                    'You are currently using the e2e cookie as an access token. If you would like to test logout on staging you will need to clear your cookies'
                );
            },
            user,
            error: null,
        };
    }, [accessToken, userId, NEXT_PUBLIC_ENV, isLoading]);

    return (
        <E2eAuthContext.Provider value={value}>
            {children}
        </E2eAuthContext.Provider>
    );
};
